import { useState, useEffect } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { useApp } from '../context/AppContext';
import { Plus, Edit, Trash2, X, ChevronDown, ChevronRight, FolderOpen } from 'lucide-react';
import { adminApi, CategoryWithSubcategories, CategoryData, SubcategoryData } from '../api/admin';
import AdminLayout from '../components/AdminLayout';

interface CategoryFormData {
  slug: string;
  name_fr: string;
  name_en: string;
}

interface SubcategoryFormData {
  slug: string;
  name_fr: string;
  name_en: string;
  category_id: number;
}

const emptyCategory: CategoryFormData = { slug: '', name_fr: '', name_en: '' };
const emptySubcategory: SubcategoryFormData = { slug: '', name_fr: '', name_en: '', category_id: 0 };

export default function AdminCategories() {
  usePageTitle("Administration — Catégories — Lodiss");
  const { lang } = useApp();

  const [categories, setCategories] = useState<CategoryWithSubcategories[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedCats, setExpandedCats] = useState<Set<number>>(new Set());
  const [showCatModal, setShowCatModal] = useState(false);
  const [showSubcatModal, setShowSubcatModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CategoryWithSubcategories | null>(null);
  const [editingSubcategory, setEditingSubcategory] = useState<SubcategoryData | null>(null);
  const [catForm, setCatForm] = useState<CategoryFormData>(emptyCategory);
  const [subcatForm, setSubcatForm] = useState<SubcategoryFormData>(emptySubcategory);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: 'category' | 'subcategory'; id: number; name: string } | null>(null);

  const loadCategories = () => {
    setLoading(true);
    adminApi.getCategories()
      .then(setCategories)
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const toggleExpand = (catId: number) => {
    setExpandedCats(prev => {
      const next = new Set(prev);
      if (next.has(catId)) {
        next.delete(catId);
      } else {
        next.add(catId);
      }
      return next;
    });
  };

  const handleSaveCategory = async () => {
    if (!catForm.slug || !catForm.name_fr) return;
    setSaving(true);
    try {
      if (editingCategory) {
        await adminApi.updateCategory(editingCategory.id, catForm);
      } else {
        await adminApi.createCategory(catForm);
      }
      setShowCatModal(false);
      setEditingCategory(null);
      setCatForm(emptyCategory);
      loadCategories();
    } catch (err) {
      console.error('Failed to save category:', err);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSubcategory = async () => {
    if (!subcatForm.slug || !subcatForm.name_fr || !subcatForm.category_id) return;
    setSaving(true);
    try {
      if (editingSubcategory) {
        await adminApi.updateSubcategory(editingSubcategory.id, subcatForm);
      } else {
        await adminApi.createSubcategory(subcatForm);
      }
      setShowSubcatModal(false);
      setEditingSubcategory(null);
      setSubcatForm(emptySubcategory);
      loadCategories();
    } catch (err) {
      console.error('Failed to save subcategory:', err);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      if (deleteConfirm.type === 'category') {
        await adminApi.deleteCategory(deleteConfirm.id);
      } else {
        await adminApi.deleteSubcategory(deleteConfirm.id);
      }
      setDeleteConfirm(null);
      loadCategories();
    } catch (err: any) {
      alert(err.message || 'Erreur lors de la suppression');
    }
  };

  const openEditCategory = (cat: CategoryWithSubcategories) => {
    setEditingCategory(cat);
    setCatForm({ slug: cat.slug, name_fr: cat.name_fr, name_en: cat.name_en });
    setShowCatModal(true);
  };

  const openEditSubcategory = (subcat: SubcategoryData) => {
    setEditingSubcategory(subcat);
    setSubcatForm({ slug: subcat.slug, name_fr: subcat.name_fr, name_en: subcat.name_en, category_id: subcat.category_id });
    setShowSubcatModal(true);
  };

  const openNewSubcategory = (categoryId: number) => {
    setEditingSubcategory(null);
    setSubcatForm({ ...emptySubcategory, category_id: categoryId });
    setShowSubcatModal(true);
  };

  return (
    <AdminLayout>
      <div>
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Catégories</h1>
          <button
            onClick={() => { setEditingCategory(null); setCatForm(emptyCategory); setShowCatModal(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-lodiss-red text-white rounded-lg hover:bg-lodiss-dark"
          >
            <Plus size={18} />
            Nouvelle catégorie
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-lodiss-red border-t-transparent rounded-full animate-spin" />
          </div>
        ) : categories.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <FolderOpen size={48} className="mx-auto mb-4 text-gray-300" />
            <p>Aucune catégorie</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {categories.map(cat => (
              <div key={cat.id} className="border-b border-gray-100 last:border-0">
                {/* Category Row */}
                <div className="flex items-center justify-between p-4 hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => toggleExpand(cat.id)}
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      {expandedCats.has(cat.id) ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    </button>
                    <div>
                      <p className="font-medium text-gray-900">{cat.name_fr}</p>
                      <p className="text-xs text-gray-400">{cat.slug} · {cat.subcategories.length} sous-catégorie(s)</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openNewSubcategory(cat.id)}
                      className="p-2 text-gray-400 hover:text-lodiss-red hover:bg-red-50 rounded"
                      title="Ajouter sous-catégorie"
                    >
                      <Plus size={16} />
                    </button>
                    <button
                      onClick={() => openEditCategory(cat)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => setDeleteConfirm({ type: 'category', id: cat.id, name: cat.name_fr })}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                      disabled={cat.subcategories.length > 0}
                      title={cat.subcategories.length > 0 ? 'Supprimez d\'abord les sous-catégories' : 'Supprimer'}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                {/* Subcategories */}
                {expandedCats.has(cat.id) && cat.subcategories.length > 0 && (
                  <div className="bg-gray-50 border-t border-gray-100">
                    {cat.subcategories.map(subcat => (
                      <div
                        key={subcat.id}
                        className="flex items-center justify-between px-8 py-3 border-b border-gray-200 last:border-0 hover:bg-gray-100"
                      >
                        <div>
                          <p className="text-sm text-gray-900">{subcat.name_fr}</p>
                          <p className="text-xs text-gray-400">{subcat.slug}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openEditSubcategory(subcat)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm({ type: 'subcategory', id: subcat.id, name: subcat.name_fr })}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Category Modal */}
      {showCatModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-bold">
                {editingCategory ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
              </h2>
              <button onClick={() => setShowCatModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X size={20} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Slug *</label>
                <input
                  type="text"
                  value={catForm.slug}
                  onChange={e => setCatForm({ ...catForm, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                  placeholder="vetements"
                />
                <p className="text-xs text-gray-400 mt-1">URL-friendly (ex: vetements, epi, batteries)</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom (FR) *</label>
                <input
                  type="text"
                  value={catForm.name_fr}
                  onChange={e => setCatForm({ ...catForm, name_fr: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                  placeholder="Vêtements"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom (EN)</label>
                <input
                  type="text"
                  value={catForm.name_en}
                  onChange={e => setCatForm({ ...catForm, name_en: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                  placeholder="Clothing"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => setShowCatModal(false)}
                  className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSaveCategory}
                  disabled={saving || !catForm.slug || !catForm.name_fr}
                  className="px-4 py-2 bg-lodiss-red text-white rounded-lg hover:bg-lodiss-dark disabled:opacity-50"
                >
                  {saving ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subcategory Modal */}
      {showSubcatModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-bold">
                {editingSubcategory ? 'Modifier la sous-catégorie' : 'Nouvelle sous-catégorie'}
              </h2>
              <button onClick={() => setShowSubcatModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X size={20} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie parente *</label>
                <select
                  value={subcatForm.category_id}
                  onChange={e => setSubcatForm({ ...subcatForm, category_id: Number(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                >
                  <option value={0}>Sélectionner</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name_fr}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Slug *</label>
                <input
                  type="text"
                  value={subcatForm.slug}
                  onChange={e => setSubcatForm({ ...subcatForm, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                  placeholder="industrie-btp"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom (FR) *</label>
                <input
                  type="text"
                  value={subcatForm.name_fr}
                  onChange={e => setSubcatForm({ ...subcatForm, name_fr: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                  placeholder="Industrie & BTP"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom (EN)</label>
                <input
                  type="text"
                  value={subcatForm.name_en}
                  onChange={e => setSubcatForm({ ...subcatForm, name_en: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                  placeholder="Industry & Construction"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => setShowSubcatModal(false)}
                  className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSaveSubcategory}
                  disabled={saving || !subcatForm.slug || !subcatForm.name_fr || !subcatForm.category_id}
                  className="px-4 py-2 bg-lodiss-red text-white rounded-lg hover:bg-lodiss-dark disabled:opacity-50"
                >
                  {saving ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-sm p-6">
            <h3 className="text-lg font-bold mb-2">Confirmer la suppression</h3>
            <p className="text-gray-600 mb-6">
              Voulez-vous vraiment supprimer "{deleteConfirm.name}" ?
              {deleteConfirm.type === 'category' && (
                <span className="block mt-2 text-red-600 text-sm">
                  Attention : Cela supprimera aussi toutes les sous-catégories.
                </span>
              )}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
